import { UI, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../../../hooks/useRecoilQuery';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../../../routes/types';
import { publicOfferAssetToInvestQuery } from '../../../../../../../../store/product/selectors';
import { PublicOfferDetailsModalityWithTimelineNormalized } from '../../../../../../../../store/product/types';
import { sessionState } from '../../../../../../../../store/session/atom';
import { CardHeader } from '../../components/CardHeader';
import { ConfirmationButton } from '../../components/ConfirmationButton';
import { LegacyApplicationSuccessDetails } from '../../components/LegacyApplicationSuccessDetails';
import { DetailsItemProps } from '../../components/TicketApplicationSuccessDetails';
import { CustomerApplicationModalProductProps } from '../../types';

import { PublicOfferConfirmation } from './components/PublicOfferConfirmation';
import { PublicOfferDocuments } from './components/PublicOfferDocuments';
import { PublicOfferInvestCard } from './components/PublicOfferInvestCard';
import { PublicOfferRules } from './components/PublicOfferRules';
import { getDisabledReason, managerNoticeTexts } from './constants';

export interface PublicOfferApplicationProps
  extends CustomerApplicationModalProductProps<'publicOffers'> {
  onClose: () => void;
}

type Modality = PublicOfferDetailsModalityWithTimelineNormalized;

export interface PublicOfferUserSelectionValues {
  currentValue?: number;
  currentEquityValue?: number;
  amountItem?: number;
  currentRemuneration?: number;
  useMarketValueEquity?: boolean;
  useMarketValueRemuneration?: boolean;
  hasError?: boolean;
}

export const PublicOfferApplication: React.FC<PublicOfferApplicationProps> = ({
  offer,
  onClose,
  accountId,
  cpfCnpjFormatted,
  cpfCnpj,
}) => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'CustomerApplicationModal'>>();
  const { userProfile } = useRecoilValue(sessionState);
  const isManager = !!(userProfile?.isGestor && userProfile?.idTipoParceiro !== 4);

  const [managerTermsAccepted, setManagerTermsAccepted] = useState(false);
  const [successInfos, setSuccessInfos] = useState<DetailsItemProps[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedModality, setSelectedModality] = useState<Modality>();
  const [userSelectionValues, setUserSelectionValues] = useState<PublicOfferUserSelectionValues>(
    {}
  );

  const { data: publicOfferAssetDetails, loading: publicOfferAssetDetailsLoading } = useRecoilQuery(
    publicOfferAssetToInvestQuery({
      offerId: offer.offerId,
      accountId,
      cpf: cpfCnpjFormatted || cpfCnpj.toString(),
    })
  );

  const handleSuccess = (infos: DetailsItemProps[]) => {
    setSuccessInfos(infos);
    setCurrentStep(3);
  };

  const handleFollowTicket = () => {
    navigation.replace('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: {
          ...params,
          selectedTab: 'transactions',
          movementInitialTab: 'PublicOffering',
        },
      },
    });
  };

  const managerDidNotAcceptTerms = isManager && !managerTermsAccepted;
  const isDisabled =
    !!userSelectionValues?.hasError ||
    !selectedModality ||
    selectedModality.isExpired ||
    managerDidNotAcceptTerms;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <PublicOfferInvestCard
              offer={offer}
              setUserSelectionValues={setUserSelectionValues}
              selectedModality={selectedModality}
              setSelectedModality={setSelectedModality}
              publicOfferAssetDetails={publicOfferAssetDetails}
              publicOfferAssetDetailsLoading={publicOfferAssetDetailsLoading}
            />

            {!!isManager && (
              <UI.CheckableNotice
                isSelected={managerTermsAccepted}
                onCheckboxChange={setManagerTermsAccepted}
                textObjects={managerNoticeTexts}
              />
            )}

            <PublicOfferRules
              publicOfferAssetDetailsLoading={publicOfferAssetDetailsLoading}
              publicOfferAssetDetails={publicOfferAssetDetails}
            />
            <PublicOfferDocuments offer={offer} />

            <ConfirmationButton
              onConfirm={() => setCurrentStep(2)}
              disabled={isDisabled}
              disabledReason={getDisabledReason(selectedModality, managerDidNotAcceptTerms)}
              buttonText="Solicitar reserva"
            />
          </>
        );

      case 2:
        return (
          <PublicOfferConfirmation
            offer={offer}
            accountId={accountId}
            selectedModality={selectedModality!}
            userSelectionValues={userSelectionValues}
            publicOfferAssetDetails={publicOfferAssetDetails}
            onClose={onClose}
            onSuccess={handleSuccess}
            isManager={isManager}
          />
        );

      case 3:
        return (
          <LegacyApplicationSuccessDetails
            title="Pedido de reserva realizado"
            orderDetails={successInfos}
            onClose={onClose}
            onFollow={handleFollowTicket}
          />
        );
      default:
        return null;
    }
  };

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <CardHeader onClose={onClose} hasSteps totalSteps={3} currentStep={currentStep} />

      {renderStep()}
    </VStack>
  );
};
